import React, { useCallback, useEffect, useState } from 'react';
import { collection, onSnapshot, query, where, limit } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { LIVE_STREAM_API_BASE } from '../config/liveStream';
import {
  ensureLiveStreamPairingKey,
  regenerateLiveStreamPairingKey,
} from '../utils/liveStreamPairing';
import './LiveRunsPage.css';

function formatTime(value) {
  if (!value) return '—';
  const d = value.toDate ? value.toDate() : new Date(value);
  return d.toLocaleString();
}

function LivePotCard({ title, latest }) {
  const isFront = title === 'Front';
  const mm = isFront ? latest?.front_mm : latest?.rear_mm;
  const raw = isFront ? latest?.front : latest?.rear;
  const mv = isFront ? latest?.front_mv : latest?.rear_mv;
  return (
    <div className="live-runs-pot">
      <h3>{title}</h3>
      <div className="live-runs-pot-value">
        {typeof mm === 'number' ? `${mm.toFixed(1)} mm` : '—'}
      </div>
      <p className="live-runs-pot-sub">
        {typeof raw === 'number' ? `ADC ${raw}` : '—'}
        {typeof mv === 'number' ? ` · ${mv} mV` : ''}
      </p>
    </div>
  );
}

const LiveRunsPage = () => {
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);
  const [pairingKey, setPairingKey] = useState('');
  const [pairingBusy, setPairingBusy] = useState(false);
  const [pairingError, setPairingError] = useState(null);
  const [runsError, setRunsError] = useState(null);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const q = query(
      collection(db, 'liveRuns'),
      where('userId', '==', user.uid),
      limit(20)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => {
          const ta = a.updatedAt?.toMillis?.() || 0;
          const tb = b.updatedAt?.toMillis?.() || 0;
          return tb - ta;
        });
        setRuns(list);
        setRunsError(null);
      },
      (err) => {
        console.error('liveRuns snapshot', err);
        setRunsError(err?.message || 'Could not load live runs');
      }
    );
    return () => unsub();
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    ensureLiveStreamPairingKey(user.uid)
      .then((code) => {
        setPairingKey(code);
        setPairingError(null);
      })
      .catch((err) => setPairingError(err?.message || 'Could not load pairing key'));
  }, [user?.uid]);

  const onRegenerateKey = useCallback(async () => {
    if (!user?.uid || pairingBusy) return;
    setPairingBusy(true);
    try {
      const code = await regenerateLiveStreamPairingKey(user.uid);
      setPairingKey(code);
      setPairingError(null);
    } catch (err) {
      setPairingError(err?.message || 'Failed to regenerate key');
    } finally {
      setPairingBusy(false);
    }
  }, [user, pairingBusy]);

  const liveRuns = runs.filter((r) => r.status === 'live');
  const active = liveRuns[0];

  return (
    <div className="live-runs-page">
      <h1>Live runs</h1>
      <p className="live-runs-desc">
        Stream suspension data from your datalogger while recording. Enter the pairing key and API URL on the device
        under Settings → Live stream.
      </p>

      {pairingError ? (
        <div className="live-runs-error" role="alert">
          {pairingError}
        </div>
      ) : null}
      {runsError ? (
        <div className="live-runs-error" role="alert">
          Live runs: {runsError}
        </div>
      ) : null}

      <section className="live-runs-section">
        <h2>Pairing</h2>
        <p className="live-runs-meta">
          API base URL (default): <code>{LIVE_STREAM_API_BASE}</code>
        </p>
        <p className="live-runs-meta">
          Pairing key:{' '}
          <code className="live-runs-key">{pairingKey || '…'}</code>
          <button type="button" className="live-runs-btn secondary" onClick={onRegenerateKey} disabled={pairingBusy}>
            {pairingBusy ? '…' : 'Regenerate'}
          </button>
        </p>
      </section>

      <section className="live-runs-section">
        <h2>{liveRuns.length ? 'Live now' : 'No live run'}</h2>
        {active ? (
          <div className="live-runs-active">
            <p className="live-runs-meta">
              <span className="live-runs-badge">LIVE</span>
              {active.fileName || active.id} · {active.sampleCount || 0} samples · updated{' '}
              {formatTime(active.updatedAt)}
            </p>
            <div className="live-runs-pots">
              <LivePotCard title="Front" latest={active.latest} />
              <LivePotCard title="Rear" latest={active.latest} />
            </div>
          </div>
        ) : (
          <p className="live-runs-empty">Start a recording on the device with streaming enabled to see live values here.</p>
        )}
      </section>

      {runs.length > 0 ? (
        <section className="live-runs-section">
          <h2>Recent sessions</h2>
          <ul className="live-runs-list">
            {runs.map((r) => (
              <li key={r.id} className="live-runs-list-item">
                <span className={`live-runs-status live-runs-status--${r.status || 'unknown'}`}>{r.status}</span>
                <span>{r.fileName || r.id}</span>
                <span className="live-runs-list-meta">{r.sampleCount || 0} samples</span>
                <span className="live-runs-list-meta">{formatTime(r.updatedAt)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
};

export default LiveRunsPage;
